import { generateClientRoster, CLIENT_SECTORS, CLIENT_TYPOLOGIES, ARGUMENTS } from '../../data/clients';
import { COMPETITORS } from '../../data/competitors';

// Util — small deterministic-ish PRNG using Math.random for now.
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pickN = (arr, n) => {
  const copy = [...arr];
  const result = [];
  for (let i = 0; i < n && copy.length > 0; i++) {
    result.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]);
  }
  return result;
};

/**
 * Market intelligence slice — owns the client roster, the contracts that the
 * competitors currently hold, and the player's challenge attempts (debauchage).
 *
 * Unlocked when both Marketing senior (Camille R.) AND a commercial role are
 * hired (any of role-lea-bertrand / role-marc-dupuis / role-sylvie-marchand).
 */
export const createMarketIntelSlice = (set, get) => ({
  // Persistent state
  clientRoster: [],
  competitorContracts: [],   // { id, clientId, competitorId, value, daysRemaining, category }
  challenges: [],            // { id, clientId, competitorContractId, argument, day, endDay, status }
  challengeHistory: [],      // resolved challenges, max 15 entries (success + failed)
  challengesThisMonth: 0,
  lastChallengeMonth: 0,

  /**
   * Returns true when the player can access the "Veille concurrents" feature.
   * Requires: Marketing senior (Camille) + any Commercial role.
   */
  hasMarketIntelAccess: () => {
    const hired = (get().hiredSupport || {});
    const hasMarketing = !!hired['role-sherazade-fetitou'];
    const hasCommercial = !!hired['role-lea-bertrand']
      || !!hired['role-marc-dupuis']
      || !!hired['role-sylvie-marchand'];
    return hasMarketing && hasCommercial;
  },

  /**
   * Build the master roster + assign each competitor a handful of contracts.
   * Called lazily on first access; idempotent.
   */
  ensureMarketDataInitialized: () => {
    const state = get();
    if (state.clientRoster && state.clientRoster.length > 0) return;
    const roster = generateClientRoster();
    // Each of the 5 competitors gets 3-5 client contracts.
    const competitorContracts = [];
    COMPETITORS.forEach((comp, compIdx) => {
      const compatibleClients = roster.filter(c => {
        // Spread roughly by competitor specialty index.
        return (c.id.length + compIdx) % 4 !== 3;
      });
      const owned = pickN(compatibleClients, randInt(3, 5));
      owned.forEach(client => {
        competitorContracts.push({
          id: `${comp.id}-${client.id}`,
          clientId: client.id,
          competitorId: comp.id,
          competitorName: comp.name,
          category: comp.category || 'Messagerie / Colis',
          value: randInt(800, 4500),
          daysRemaining: randInt(15, 60),
        });
      });
    });
    set({ clientRoster: roster, competitorContracts });
  },

  /**
   * Decrement competitor contracts daily; renew finished ones on a new pool.
   * Called from coreSlice.nextDay (working days only).
   */
  tickMarketIntel: () => {
    const state = get();
    if (!state.clientRoster || state.clientRoster.length === 0) return;
    const updated = state.competitorContracts.map(c => ({
      ...c,
      daysRemaining: Math.max(0, c.daysRemaining - 1),
    })).filter(c => c.daysRemaining > 0);
    // Replenish: 1 in 8 chance to add a fresh contract.
    if (Math.random() < 0.12 && state.clientRoster.length > 0) {
      const client = state.clientRoster[Math.floor(Math.random() * state.clientRoster.length)];
      const comp = COMPETITORS[Math.floor(Math.random() * COMPETITORS.length)];
      updated.push({
        id: `${comp.id}-${client.id}-${Date.now()}`,
        clientId: client.id,
        competitorId: comp.id,
        competitorName: comp.name,
        category: comp.category || 'Messagerie / Colis',
        value: randInt(900, 4200),
        daysRemaining: randInt(20, 55),
      });
    }
    set({ competitorContracts: updated });

    // Resolve pending challenges that reached endDay.
    const stillOngoing = [];
    const justResolved = [];
    state.challenges.forEach(ch => {
      if (ch.status !== 'pending') {
        stillOngoing.push(ch);
        return;
      }
      if ((state.totalDays || 0) + 1 >= ch.endDay) {
        // Run resolution logic
        justResolved.push(ch);
      } else {
        stillOngoing.push(ch);
      }
    });
    if (justResolved.length > 0) {
      const stillPending = [...stillOngoing];
      const newHistoryEntries = [];
      justResolved.forEach(ch => {
        const client = state.clientRoster.find(c => c.id === ch.clientId);
        if (!client) {
          newHistoryEntries.push({ ...ch, status: 'failed', reason: 'client_lost', resolvedAt: (state.totalDays || 0) + 1, clientName: '—' });
          return;
        }
        const typology = CLIENT_TYPOLOGIES.find(t => t.id === client.typologyId);
        const switchDifficulty = typology ? typology.switchDifficulty : 0.5;
        // Argument match check
        const driverMatch =
          ch.argument === client.primaryDriver ? 1.0 :
          ch.argument === client.secondaryDriver ? 0.6 : 0.15;
        // Player attractiveness factor
        const noto = state.notoriety || 10;
        const rep  = state.reputation || 50;
        const score = (noto * 0.5 + rep * 0.5) * driverMatch - (client.loyalty * switchDifficulty);
        // Final roll: positive score → success; negative → fail; near 0 → 50/50.
        let successProb = Math.max(0.05, Math.min(0.92, 0.5 + score / 100));
        // Intensive démarche grants a flat +20 % to success probability (capped at 95 %).
        if (ch.intensity === 'intensive') {
          successProb = Math.min(0.95, successProb + 0.20);
        }
        const success = Math.random() < successProb;
        const result = {
          ...ch,
          status: success ? 'success' : 'failed',
          successProb: Math.round(successProb * 100),
          resolvedAt: (state.totalDays || 0) + 1,
          clientName: client.name,
          contract: success ? {
            clientName: client.name,
            value: ch.contractValue,
            durationDays: randInt(30, 60),
          } : null,
        };
        // Success entries also stay in the pending list until the player
        // accepts the contract (then `acceptChallengedContract` removes them).
        if (success) stillPending.push(result);
        newHistoryEntries.push(result);

        // Push a notification
        const notif = {
          id: `${ch.id}-resolved`,
          type: success ? 'challenge_won' : 'challenge_lost',
          title: success ? 'Contrat capté !' : 'Démarchage échoué',
          message: success
            ? `${client.name} accepte de rompre son contrat avec ${ch.competitorName} pour vous rejoindre.`
            : `${client.name} a finalement décliné votre offre (${ch.competitorName} conserve le contrat).`,
          day: (state.totalDays || 0) + 1,
          read: false,
        };
        set(prev => ({
          notifications: [...(prev.notifications || []).slice(-30), notif],
        }));
      });
      const newHistory = [...(state.challengeHistory || []), ...newHistoryEntries].slice(-15);
      set({ challenges: stillPending, challengeHistory: newHistory });
    }
  },

  /**
   * Start a contract challenge. Two intensity levels:
   *   - 'standard'  : 800 € upfront, 4 days, base success probability.
   *   - 'intensive' : 1 600 € upfront, 4 days, +20 % success probability.
   * Returns { ok, reason }.
   */
  challengeContract: (competitorContractId, argument, intensity = 'standard') => {
    const state = get();
    if (!get().hasMarketIntelAccess()) {
      return { ok: false, reason: 'no_access' };
    }
    const cost = intensity === 'intensive' ? 1600 : 800;
    if (state.treasury < cost) return { ok: false, reason: 'insufficient_funds' };

    // Monthly cap: 3 challenges / month.
    const currentMonth = state.month || 1;
    let challengesThisMonth = state.challengesThisMonth || 0;
    if (state.lastChallengeMonth !== currentMonth) challengesThisMonth = 0;
    if (challengesThisMonth >= 3) return { ok: false, reason: 'monthly_cap' };

    const contract = state.competitorContracts.find(c => c.id === competitorContractId);
    if (!contract) return { ok: false, reason: 'contract_not_found' };

    const validArgument = ARGUMENTS.find(a => a.id === argument);
    if (!validArgument) return { ok: false, reason: 'invalid_argument' };

    const ch = {
      id: `ch-${Date.now()}`,
      clientId: contract.clientId,
      competitorContractId,
      competitorName: contract.competitorName,
      argument,
      intensity,
      day: state.totalDays,
      endDay: state.totalDays + 4,
      status: 'pending',
      contractValue: contract.value,
    };

    set({
      challenges: [...(state.challenges || []), ch],
      treasury: state.treasury - cost,
      challengesThisMonth: challengesThisMonth + 1,
      lastChallengeMonth: currentMonth,
      // Remove the contract from the competitor's pool so it isn't double-attacked.
      competitorContracts: state.competitorContracts.filter(c => c.id !== competitorContractId),
    });
    return { ok: true };
  },

  /**
   * Convert a successful challenge into an availableContract entry the player
   * can accept. Removes the challenge afterwards.
   */
  acceptChallengedContract: (challengeId) => {
    const state = get();
    const ch = state.challenges.find(c => c.id === challengeId);
    if (!ch || ch.status !== 'success' || !ch.contract) {
      return { ok: false, reason: 'not_ready' };
    }
    const client = state.clientRoster.find(c => c.id === ch.clientId);
    if (!client) return { ok: false, reason: 'client_lost' };

    const instanceId = `chal-${ch.id}`;
    const newContract = {
      instanceId,
      name: `${client.name} — Reprise de contrat`,
      category: 'Messagerie / Colis',
      frequency: 'régulier',
      tierMin: 1,
      duration: ch.contract.durationDays,
      dailyRevenue: ch.contract.value,
      revenue: ch.contract.value,
      origin: 'challenge',
      clientId: ch.clientId,
    };
    set({
      availableContracts: [...(state.availableContracts || []), newContract],
      challenges: state.challenges.filter(c => c.id !== challengeId),
    });
    return { ok: true, instanceId };
  },

  /**
   * Attend a regional trade show. Costs 1 000 €, grants +5 notoriety.
   * Unlock: `salons` (Marc Dupuis).
   */
  attendTradeShow: () => {
    const state = get();
    if (typeof state.hasSupportUnlock !== 'function' || !state.hasSupportUnlock('salons')) {
      return { ok: false, reason: 'no_unlock' };
    }
    if ((state.treasury || 0) < 1000) return { ok: false, reason: 'insufficient_funds' };
    if ((state.tradeShowMonth || -1) === (state.month || 1)) {
      return { ok: false, reason: 'already_attended' };
    }
    set({
      treasury: state.treasury - 1000,
      notoriety: Math.max(0, Math.min(100, (state.notoriety || 0) + 5)),
      tradeShowMonth: state.month || 1,
      notifications: [
        ...(state.notifications || []).slice(-30),
        {
          id: `salon-${Date.now()}`,
          type: 'tradeshow',
          title: 'Participation salon',
          message: 'Vous avez participé au salon régional du transport. Notoriété +5.',
          day: state.totalDays || 0,
          read: false,
        },
      ],
    });
    return { ok: true };
  },

  // Helper for the UI to know if monthly cap reached / reset.
  getChallengesUsedThisMonth: () => {
    const state = get();
    const currentMonth = state.month || 1;
    if (state.lastChallengeMonth !== currentMonth) return 0;
    return state.challengesThisMonth || 0;
  },
});

export { CLIENT_SECTORS, CLIENT_TYPOLOGIES, ARGUMENTS };
